import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useProjectAdminImpersonation,
  type ProjectAdminUser,
} from "@/contexts/ProjectAdminImpersonationContext";
import { supabase } from "@/integrations/supabase/client";
import { UserSearch } from "lucide-react";
import { SelectorVerComo } from "@/components/ui/selector-ver-como";

/**
 * Selector "Ver como" para el Portal Estructura de Comisiones.
 * Visible solo para Super Admin (rol 1). Lista usuarios con rol Administrador
 * de Proyecto (rol_id = 2).
 *
 * Se pinta con el selector estándar de los portales (SelectorVerComo).
 */
export function ProjectAdminImpersonationSelector({ className, compacto }: { className?: string; compacto?: boolean }) {
  const { profile } = useAuth();
  const { impersonatedUser, setImpersonatedUser, clearImpersonation, isImpersonating } =
    useProjectAdminImpersonation();
  const [users, setUsers] = useState<ProjectAdminUser[]>([]);
  const [loading, setLoading] = useState(false);

  const canImpersonate = profile?.puede_impersonar === true;

  useEffect(() => {
    if (!canImpersonate) return;
    setLoading(true);
    (async () => {
      // `usuarios` no tiene columna `id` (la PK es `email`): pedirla daba 42703 y la lista
      // salía vacía. El identificador del impersonado es el correo.
      const { data, error } = await supabase
        .from("usuarios")
        .select("email, nombre, rol_id")
        .eq("rol_id", 2)
        .eq("activo", true)
        .order("nombre");
      if (error) console.error("Error al cargar administradores de proyecto:", error);
      if (data) {
        setUsers(
          data.map((u: any) => ({
            id: u.email,
            nombre: u.nombre || u.email,
            email: u.email,
            rol_nombre: "Administrador de Proyecto",
          })),
        );
      }
      setLoading(false);
    })();
  }, [canImpersonate]);

  if (!canImpersonate) return null;

  return (
    <SelectorVerComo
      className={className}
      compacto={compacto}
      etiqueta={isImpersonating ? impersonatedUser?.nombre || impersonatedUser?.email || "Administrador" : "Super Admin"}
      impersonando={isImpersonating}
      iconoVistaPropia={<UserSearch className="size-3.5" strokeWidth={1.75} />}
      opcionPorDefecto={{
        key: "__default__",
        label: "Super Admin",
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <UserSearch className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={[{
        titulo: "Administradores de proyecto",
        opciones: users.map((u) => ({
          key: u.id,
          label: u.nombre,
          buscarPor: u.email,
          seleccionada: impersonatedUser?.id === u.id,
          onSelect: () => setImpersonatedUser(u),
        })),
      }]}
      onLimpiar={clearImpersonation}
      ariaLimpiar="Volver a Super Admin"
      textoVacio="Ningún administrador coincide."
      cargando={loading}
    />
  );
}
